import { useEffect, useRef, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { selectFilters } from "store/filters/selectors";
import {
  updateFilters,
  deleteFilters,
  updateAndDeleteFilters,
} from "store/filters/operations";
import { Checkbox } from "reusableComponents";
import { useTranslation } from "react-i18next";
import { getPriceTypes } from "helpers/getPriceTypes";
import FiltersButton from "../FiltersButton";
import styles from "./styles.module.scss";

const PriceTypeFilterButton = () => {
  const dispatch = useDispatch();
  const [t] = useTranslation("global");
  const filters = useSelector(selectFilters);
  const [showPriceDropdown, setShowPriceDropdown] = useState(false);
  const selectedUsage = filters.usages?.[0]; // Get the first selected usage
  const [priceOptions, setPriceOptions] = useState();
  const { min_price, max_price, usages } = filters; // Get min_price and max_price from filters
  const [selectedPriceOption, setSelectedPriceOption] = useState(null); // Track the selected price option
  const dropdownRef = useRef(null);

  useEffect(() => {
    setPriceOptions(getPriceTypes(selectedUsage));
  }, [usages]);

  const toggleShowPriceDropdown = () =>
    setShowPriceDropdown(!showPriceDropdown);

  const handleSelectPriceOption = (option) => {
    if (option.type == filters.price_type) {
      dispatch(deleteFilters("min_price", "max_price", "price_type"));
      setSelectedPriceOption(null);
    } else {
      setSelectedPriceOption(option);
      dispatch(
        updateAndDeleteFilters({ price_type: option.type }, [
          "min_price",
          "max_price",
        ])
      );
    }
  };

  const handlePriceChange = (priceRange) => {
    const { min, max } = priceRange; // Extract the min and max directly from the price object
    dispatch(updateFilters({ min_price: min, max_price: max }));
  };

  // Handle outside click to close dropdown
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setShowPriceDropdown(false); // Close the dropdown when clicking outside
      }
    };

    if (showPriceDropdown) {
      document.addEventListener("mousedown", handleClickOutside);
    } else {
      document.removeEventListener("mousedown", handleClickOutside);
    }

    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, [showPriceDropdown]);

  return (
    <div className={styles.priceWrapper} ref={dropdownRef}>
      <FiltersButton
        label={t("search.price")}
        isActive={filters.price_type}
        onClick={toggleShowPriceDropdown}
      />
      {showPriceDropdown && (
        <div className={styles.priceDropdownBody}>
          {!selectedUsage && (
            <p className={styles.noValuesText}>
              {t("search.priceMissingValues")}
            </p>
          )}

          {selectedUsage && priceOptions.length > 0 && (
            <>
              {/* Step 1: Render price options (types) */}
              <div className={styles.priceOptionList}>
                {priceOptions.map((option) => (
                  <div key={option.type} className={styles.priceOption}>
                    <Checkbox
                      onChange={() => handleSelectPriceOption(option)}
                      checked={filters.price_type == option.type}
                      label={t(option.key)}
                    />
                  </div>
                ))}
              </div>
              <hr />

              {/* Step 2: Render prices for the selected price option */}
              {filters.price_type && (
                <div className={styles.selectedPriceList}>
                  {selectedPriceOption?.price.map((priceRange, index) => (
                    <div key={index} className={styles.priceOption}>
                      <Checkbox
                        checked={
                          min_price === priceRange.min &&
                          max_price === priceRange.max
                        }
                        onChange={() => handlePriceChange(priceRange)}
                        label={
                          priceRange.max === null
                            ? `+ ${priceRange.min} €`
                            : `${t("search.bar.upTo")} ${priceRange.max} €`
                        }
                      />
                    </div>
                  ))}
                </div>
              )}
            </>
          )}
        </div>
      )}
    </div>
  );
};

export default PriceTypeFilterButton;
